import type { InstanceStepModelView } from "@/types";

// #P002 (UC-3): die PUREN Regeln der Karte „Modell je Schritt" — Schablone und Patch-Bau.
// Getrennt von der Komponente, weil beide auch die Seite braucht (der Speichern-Knopf gehört
// derselben Einrichtung) und weil sich reine Regeln ohne DOM prüfen lassen.

/** Der Wert im Select für „keine eigene Wahl" — leer, damit `<option value="">` greift. */
export const KEINE_WAHL = "";

/** Anforderungs-Chips: nur die STRENGE Richtung wird benannt (alles andere ist der Normalfall). */
export function anforderungsChips(zeile: InstanceStepModelView): string[] {
  const out: string[] = [];
  if (zeile.requirements.sensitiveDataOk === true) out.push("heikle Daten");
  if (zeile.requirements.hosting === "local") out.push("lokal");
  return out;
}

/**
 * Was die VORLAGE zu diesem Schritt sagt — eine Zeile, vier mögliche Aussagen.
 *
 * ⚠ `templateModel` und `processSuggestion` sind ZWEI Aussagen (Fix-Runde 1 zu UC-3): die erste
 * gilt DIESEM Schritt (Vorgabe oder Schritt-Vorschlag), die zweite „für alle Schritte" — sie ist
 * das Lauf-Modell. Sie in ein Feld zu falten hiesse, den Vorrang „Schritt vor Prozess" hier ein
 * zweites Mal auszulegen; genau daran ist die erste Fassung gescheitert.
 */
export function vorlageSatz(zeile: InstanceStepModelView): string {
  if (zeile.templateModel !== null) {
    return zeile.templateOverridable ? `Vorschlag: ${zeile.templateModel}` : `Vorgabe: ${zeile.templateModel}`;
  }
  // `?? null`: ein älterer Master liefert das Feld gar nicht — „undefined" im Satz wäre die
  // schlechteste aller Aussagen (Review 7, NIT-4).
  const prozess = zeile.processSuggestion ?? null;
  return prozess !== null ? `Prozess-Vorschlag: ${prozess}` : "—";
}

/** Trägt der Schritt eine harte Vorgabe? Dann gibt es hier nichts zu wählen. */
export function gesperrt(zeile: InstanceStepModelView): boolean {
  return zeile.templateModel !== null && !zeile.templateOverridable;
}

/**
 * Der Satz unter der Zeile: WOMIT läuft dieser Schritt — und WER hat das entschieden.
 *
 * ⚠ Gerechnet wird auf dem BERÜHRTEN Stand, nicht auf `effective` allein: der Master hat den
 * Wert vor der Änderung geliefert, und ein Satz, der die eigene Eingabe nicht abbildet, wäre
 * genau in dem Moment falsch, in dem jemand hinschaut.
 *
 * ⚠ **Ein Grenzfall bleibt bewusst ungenau** (Review 7, NIT-1): Deckt sich das Lauf-Modell
 * zufällig mit dem Prozess-Vorschlag — etwa weil der Einrichtende `instance.model` auf genau
 * denselben Namen gestellt hat —, schreibt der Satz die Wirkung dem PROZESS zu („Vorschlag des
 * Prozesses"), obwohl sie aus der Instanz-Wahl kommt. Der **Name stimmt** (und er ist die
 * Aussage, auf die es ankommt), nur die genannte STUFE ist eine von zweien. Die genaue Stufe
 * stünde erst mit einer feineren Wire-Quelle fest (`effectiveSource` kennt für die Basis nur
 * `run`, nicht ihre Sprosse) — die wird dafür bewusst nicht eingeführt: eine zusätzliche
 * Server-Zusage für einen Fall, in dem beide Antworten denselben Namen tragen.
 */
export function wirkungsSatz(zeile: InstanceStepModelView, beruehrt: string | undefined): string {
  if (gesperrt(zeile)) return `Dieser Schritt läuft auf: ${zeile.templateModel} (Vorgabe der Vorlage)`;
  const wahl = beruehrt !== undefined ? beruehrt : (zeile.chosen ?? KEINE_WAHL);
  if (wahl !== KEINE_WAHL) return `Dieser Schritt läuft auf: ${wahl} (hier gewählt)`;
  if (zeile.templateModel !== null) {
    return `Dieser Schritt läuft auf: ${zeile.templateModel} (Vorschlag der Vorlage)`;
  }
  // Der PROZESS-Vorschlag ist das Lauf-Modell — er wird als solches benannt, nicht als
  // Aussage dieses Schritts. `effective` deckt sich damit, solange niemand am Lauf wechselt.
  const prozess = zeile.processSuggestion ?? null;
  if (prozess !== null && zeile.effective === prozess) {
    return `Dieser Schritt läuft auf: ${prozess} (Vorschlag des Prozesses)`;
  }
  // Ohne eigene Aussage gilt das Lauf-Modell. Kennt der Master es nicht (kein Agent, Auskunft
  // weg), wird das GESAGT statt ein Name erfunden.
  if (zeile.effective === null) return "Dieser Schritt läuft auf: dem Lauf-Modell (noch nicht ermittelbar)";
  return `Dieser Schritt läuft auf: ${zeile.effective} (Lauf-Modell)`;
}

/**
 * Die Optionen des Selects: die erfüllenden Namen — plus ein GESPEICHERTER Wert, der nicht
 * (mehr) dabei ist. Ohne ihn sähe die Maske aus wie „keine eigene Wahl", obwohl eine
 * gespeichert ist, und das nächste Speichern nähme sie weg, ohne dass es jemand wollte
 * (Muster `mitAltwert`).
 */
export function optionen(
  zeile: InstanceStepModelView,
  beruehrt: string | undefined,
): Array<{ value: string; label: string }> {
  const basis = (zeile.erfuellend ?? []).map((name) => ({ value: name, label: name }));
  const wert = beruehrt !== undefined ? beruehrt : (zeile.chosen ?? KEINE_WAHL);
  if (wert === KEINE_WAHL || basis.some((o) => o.value === wert)) return basis;
  // „nicht mehr wählbar" nur, wenn die Auskunft BELASTBAR ist — sonst der nackte Name
  // (ein erfundenes Urteil wäre schlimmer als keines, Muster `mitAltwert`).
  return [...basis, { value: wert, label: zeile.erfuellend === null ? wert : `${wert} (nicht mehr wählbar)` }];
}

/**
 * Der Patch-Teil: **Voll-Ersatz**, aber nur wenn sich netto etwas ändert.
 *
 * `undefined` = der Schlüssel fehlt im Patch (kein Phantom-Diff — der Master ersetzt die
 * Sammlung sonst voll, und ein unberührtes Speichern nähme fremde Änderungen mit).
 */
export function baueStepModelPatch(
  zeilen: InstanceStepModelView[] | null | undefined,
  beruehrt: Record<string, string>,
  gespeichert: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!zeilen || Object.keys(beruehrt).length === 0) return undefined;
  const bestand = gespeichert ?? {};
  const naechster: Record<string, string> = {};
  for (const zeile of zeilen) {
    // Ein gesperrter Schritt nimmt nie eine Wahl auf — auch keine gespeicherte (der Sweep
    // räumt sie, und mitzuschicken hiesse, sie beim nächsten Speichern zurückzuholen).
    if (gesperrt(zeile)) continue;
    const wert = Object.hasOwn(beruehrt, zeile.stepId) ? beruehrt[zeile.stepId]! : (bestand[zeile.stepId] ?? KEINE_WAHL);
    if (wert !== KEINE_WAHL) naechster[zeile.stepId] = wert;
  }
  const gleich =
    Object.keys(naechster).length === Object.keys(bestand).length &&
    Object.entries(naechster).every(([k, v]) => bestand[k] === v);
  return gleich ? undefined : naechster;
}
